import React, { useRef } from "react";
import { Paper } from "@remo-co/ui-core/src/components/Paper";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Skeleton } from "@remo-co/ui-core/src/components/Skeleton";
import { CopyInput } from "@remo-co/ui-core/src/components/CopyInput";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import classNames from "classnames";
import logger from "logging/logger";
import { useI18n } from "i18n";
import SamlConfigurationHelperDialog from "./SamlConfigurationHelperDialog";
import { useStyles } from "./styles";
import { ISsoInfo } from "./types";

export type ISsoSamlInfo = Pick<ISsoInfo, "serviceProviderEntityID" | "acsUrl">;
interface ISamlConfigurationProps {
  isReady: boolean;
  showHelperDialog?: boolean;
  samlInfo: ISsoSamlInfo;
  tabPanelRef?: React.RefObject<HTMLDivElement>;
}

const SamlConfiguration = ({
  isReady,
  samlInfo,
  showHelperDialog,
  tabPanelRef,
}: ISamlConfigurationProps) => {
  const { t } = useI18n(["common", "sso"]);
  const classes = useStyles();
  const [isOpenDialog, setIsOpenDialog] = React.useState(true);
  const { addSuccessNotification } = useNotificationActions();
  const samlConfigContainerEl = useRef<HTMLDivElement | null>(null);
  const handleCopied = (label: string) => () => {
    addSuccessNotification({
      message: t("sso:text.copied.message", { key: label }),
      autoDismiss: 5,
    });
  };

  // Scroll to element
  React.useEffect(() => {
    if (showHelperDialog && isOpenDialog) {
      setTimeout(() => {
        try {
          if (tabPanelRef?.current && samlConfigContainerEl.current) {
            tabPanelRef?.current.scrollTo({
              top: samlConfigContainerEl.current.offsetTop - 94,
              left: 0,
              behavior: "smooth",
            });
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "unknown";
          logger.error(
            `[SamlConfiguration] could not scroll to Saml configuration element on creating new configuration. Error: ${errorMessage}`,
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHelperDialog, isOpenDialog, samlConfigContainerEl]);

  return (
    <>
      <Paper
        data-testid="saml-config-container"
        ref={samlConfigContainerEl}
        className={classNames(
          classes.section,
          classes.spacing,
          classes.samlConfigurationSection,
          { [classes.highlight]: showHelperDialog },
        )}
        elevation={0}
      >
        <Typography variant="h4">{t("sso:saml.title")}</Typography>
        <Typography
          variant="body1"
          className={classNames(
            classes.infoDescription,
            classes.descriptionSpacingBottom,
          )}
        >
          {t("sso:saml.description")}
        </Typography>

        <div className={classes.inputContainer}>
          <Typography variant="h5">
            {t("sso:service.provider.entity.id")}
          </Typography>
          {isReady ? (
            <CopyInput
              label={t("button.copy")}
              text={samlInfo.serviceProviderEntityID || ""}
              onCopy={handleCopied(t("sso:service.provider.entity.id") || "")}
            />
          ) : (
            <Skeleton
              data-testid="skeleton-1"
              variant="rectangular"
              width="100%"
              height={40}
            />
          )}
        </div>

        <div className={classes.inputContainer}>
          <Typography variant="h5">{t("sso:acs.url")}</Typography>
          {isReady ? (
            <CopyInput
              label={t("button.copy")}
              text={samlInfo.acsUrl || ""}
              onCopy={handleCopied(t("sso:acs.url") || "")}
            />
          ) : (
            <Skeleton
              data-testid="skeleton-2"
              variant="rectangular"
              width="100%"
              height={40}
            />
          )}
        </div>
      </Paper>
      {showHelperDialog && (
        <SamlConfigurationHelperDialog
          anchorEl={samlConfigContainerEl.current}
          isOpen={showHelperDialog && isOpenDialog}
          handlePopoverClose={() => setIsOpenDialog(false)}
        />
      )}
    </>
  );
};

export default SamlConfiguration;
